import React, { useState } from 'react';
import { FileText, CheckCircle, Eye, X, Save, Book, Settings, Info, GripVertical, Check, ChevronUp } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import { AvailableQuestion } from '@/services/PagesServices/AssessmentManagementService';
import './CreateAssessment.css';

const CreateAssessment: React.FC = () => {
  const { availableQuestions } = useAssessmentManagementStore();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [timeLimit, setTimeLimit] = useState('60');
  const [passingScore, setPassingScore] = useState('70');
  const [attemptsAllowed, setAttemptsAllowed] = useState('3');
  const [randomizeQuestions, setRandomizeQuestions] = useState(true);
  const [immediateFeedback, setImmediateFeedback] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState<AvailableQuestion[]>([]);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const toggleQuestionSelection = (question: AvailableQuestion) => {
    const exists = selectedQuestions.find(q => q.id === question.id);
    if (exists) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const isQuestionSelected = (questionId: number) => {
    return selectedQuestions.some(q => q.id === questionId);
  };

  const removeQuestion = (questionId: number) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.id !== questionId));
  };

  const totalPoints = selectedQuestions.reduce((sum, q) => sum + q.points, 0);

  return (
    <div className="trainer-assessment-create-assessment">
      <div className="trainer-assessment-create-layout">
        {/* Main Content */}
        <div className="trainer-assessment-create-main">

          {/* Assessment Information */}
          <div className="trainer-assessment-create-section">
            <h3>Assessment Information</h3>

            <div className="trainer-assessment-form-group">
              <label>Title *</label>
              <input
                type="text"
                placeholder="e.g., Python Programming Final Test"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="trainer-assessment-form-input"
              />
            </div>

            <div className="trainer-assessment-form-group">
              <label>Description</label>
              <textarea
                placeholder="Describe the assessment..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="trainer-assessment-form-textarea"
              />
            </div>

            <div className="trainer-assessment-settings-grid">
              <div className="trainer-assessment-form-group">
                <label>Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="trainer-assessment-form-input"
                >
                  <option value="">Select</option>
                  <option value="quiz">Quiz</option>
                  <option value="test">Test</option>
                  <option value="survey">Survey</option>
                </select>
              </div>

              <div className="trainer-assessment-form-group">
                <label>Time Limit (minutes)</label>
                <input
                  type="number"
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(e.target.value)}
                  className="trainer-assessment-form-input"
                />
              </div>
            </div>
          </div>

          {/* Questions Section */}
          <div className="trainer-assessment-create-section">
            <div className="trainer-assessment-section-header">
              <h3>Questions ({selectedQuestions.length})</h3>
              <button
                className="trainer-assessment-add-questions-btn"
                onClick={() => setShowModal(true)}
              >
                + Add Questions
              </button>
            </div>

            {/* Expandable Question Bank */}
            {showModal && (
              <div className="trainer-assessment-question-bank-expanded">
                <div className="trainer-assessment-question-bank-header">
                  <h4>Select from Question Bank</h4>
                  <button
                    className="trainer-assessment-close-bank-btn"
                    onClick={() => setShowModal(false)}
                  >
                    <ChevronUp size={20} />
                  </button>
                </div>

                <div className="trainer-assessment-available-questions-list">
                  {availableQuestions?.map((question) => (
                    <div
                      key={question.id}
                      className={`trainer-assessment-bank-question-card ${isQuestionSelected(question.id) ? 'selected' : ''}`}
                      onClick={() => toggleQuestionSelection(question)}
                    >
                      <div className="trainer-assessment-question-badges">
                        <span className="trainer-assessment-type-badge-orange">{question.type}</span>
                        <span className={`trainer-assessment-difficulty-badge-${question.difficulty}`}>
                          {question.difficulty}
                        </span>
                      </div>
                      <div className="trainer-assessment-bank-question-text">{question.question}</div>
                      {isQuestionSelected(question.id) && (
                        <div className="trainer-assessment-selected-check">
                          <Check size={20} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Questions List */}
            {selectedQuestions.length > 0 && (
              <div className="trainer-assessment-selected-questions-list">
                {selectedQuestions.map((q, index) => (
                  <div key={q.id} className="trainer-assessment-selected-question-card">
                    <div className="trainer-assessment-question-index">{index + 1}.</div>
                    <div className="trainer-assessment-selected-question-content">
                      <div className="trainer-assessment-selected-question-meta">
                        <span className="trainer-assessment-type-badge-orange">{q.type}</span>
                        <span className="trainer-assessment-points-badge">{q.points} pts</span>
                      </div>
                      <div className="trainer-assessment-selected-question-text">{q.question}</div>
                    </div>
                    <button
                      onClick={() => removeQuestion(q.id)}
                      className="trainer-assessment-remove-selected-btn"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assessment Settings */}
          <div className="trainer-assessment-create-section">
            <h3>Assessment Settings</h3>

            <div className="trainer-assessment-settings-grid">
              <div className="trainer-assessment-form-group">
                <label>Passing Score (%)</label>
                <input
                  type="number"
                  value={passingScore}
                  onChange={(e) => setPassingScore(e.target.value)}
                  className="trainer-assessment-form-input"
                />
              </div>

              <div className="trainer-assessment-form-group">
                <label>Attempts Allowed</label>
                <input
                  type="number"
                  value={attemptsAllowed}
                  onChange={(e) => setAttemptsAllowed(e.target.value)}
                  className="trainer-assessment-form-input"
                />
              </div>
            </div>

            <div className="trainer-assessment-toggle-settings">
              <div className="trainer-assessment-toggle-item">
                <div className="trainer-assessment-toggle-info">
                  <div className="trainer-assessment-toggle-label">Randomize Questions</div>
                  <div className="trainer-assessment-toggle-description">Questions appear in random order</div>
                </div>
                <label className="trainer-assessment-toggle-switch">
                  <input
                    type="checkbox"
                    checked={randomizeQuestions}
                    onChange={(e) => setRandomizeQuestions(e.target.checked)}
                  />
                  <span className="trainer-assessment-toggle-slider"></span>
                </label>
              </div>

              <div className="trainer-assessment-toggle-item">
                <div className="trainer-assessment-toggle-info">
                  <div className="trainer-assessment-toggle-label">Immediate Feedback</div>
                  <div className="trainer-assessment-toggle-description">Show correct answers after submission</div>
                </div>
                <label className="trainer-assessment-toggle-switch">
                  <input
                    type="checkbox"
                    checked={immediateFeedback}
                    onChange={(e) => setImmediateFeedback(e.target.checked)}
                  />
                  <span className="trainer-assessment-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="trainer-assessment-create-sidebar">
          {/* Actions Card */}
          <div className="trainer-assessment-sidebar-section">
            <h4>Actions</h4>
            <button className="trainer-assessment-sidebar-btn primary">
              <CheckCircle size={18} />
              Publish
            </button>
            <button className="trainer-assessment-sidebar-btn secondary">
              <Save size={18} />
              Save Draft
            </button>
            <button className="trainer-assessment-sidebar-btn secondary">
              <Eye size={18} />
              Preview
            </button>
            <button className="trainer-assessment-sidebar-btn secondary">
              <X size={18} />
              Cancel
            </button>
          </div>

          {/* Summary Card */}
          <div className="trainer-assessment-sidebar-section">
            <h4>Summary</h4>
            <div className="trainer-assessment-summary-item">
              <span className="trainer-assessment-summary-label">Questions</span>
              <span className="trainer-assessment-summary-value">{selectedQuestions.length}</span>
            </div>
            <div className="trainer-assessment-summary-item">
              <span className="trainer-assessment-summary-label">Total Points</span>
              <span className="trainer-assessment-summary-value">{totalPoints}</span>
            </div>
            <div className="trainer-assessment-summary-item">
              <span className="trainer-assessment-summary-label">Time Limit</span>
              <span className="trainer-assessment-summary-value">{timeLimit} min</span>
            </div>
            <div className="trainer-assessment-summary-item">
              <span className="trainer-assessment-summary-label">Passing Score</span>
              <span className="trainer-assessment-summary-value">{passingScore}%</span>
            </div>
          </div>

          {/* Quick Tips Card */}
          <div className="trainer-assessment-sidebar-section tips">
            <h4>Quick Tips</h4>
            <ul>
              <li>Mix question types for variety</li>
              <li>Set appropriate time limits</li>
              <li>Use randomization for tests</li>
              <li>Review all questions before publishing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAssessment;